







































































































import { computed, defineComponent, onMounted, PropType, ref } from 'vue';
import { f7 } from 'framework7-vue';

import { IRegistrationRequest } from '../types';
import { AuthType } from '@/types/enum';
import { FieldOption, Poll, PollField } from '@/modules/polls/dist/types';

import { useAppStore } from '@/store/appstore';
import { useAuthStore } from '../store';
import { usePollsStore } from '@/modules/polls/dist/store';
import { showErrorDialog } from '@/misc';
import { getFormattedDate } from '../misc';
import * as API from '@/modules/polls/dist/api';

import useValidationState from '@/validators/validation-state';

import ListInputDate from '@/components/fields/list-input-date.vue';
import ListInputName from '@/components/fields/list-input-name.vue';
import ListInputPassword from '@/components/fields/list-input-password.vue';
import ListInputSurname from '@/components/fields/list-input-surname.vue';
import ListInputEmail from '@/components/fields/list-input-email.vue';
import ListInputGender from '@/components/fields/list-input-gender.vue';
import ListInputSelect from '@/components/fields/list-input-select.vue';

export default defineComponent({
  name: 'ProfileEdit',
  components: {
    ListInputSelect,
    ListInputGender,
    ListInputDate,
    ListInputName,
    ListInputPassword,
    ListInputSurname,
    ListInputEmail,
  },
  props: {
    callback: Function as PropType<Nullable<() => void>>,
  },
  setup(props) {
    const { validationState, onStateChanged, touchAll } = useValidationState();
    const refForm = ref<HTMLElement>(null as unknown as any);
    const isDirty = computed<boolean>(() => validationState.dirty);
    const android15KeyboardHackFlag = ref<boolean>(false);

    const appStore = useAppStore();
    const authStore = useAuthStore();
    const pollsStore = usePollsStore();

    const name = ref<string>(appStore.getters.user?.fName ?? '');
    const surname = ref<string>(appStore.getters.user?.lName ?? '');
    const gender = ref<string>(appStore.getters.user?.gender ?? '');
    const birthday = ref<string | Date>(appStore.getters.user?.bday ?? '');
    const password = ref<string>('');
    const email = ref<string>(appStore.getters.user?.email ?? '');
    const birthdayFormatted = getFormattedDate(birthday.value as any);

    const buttonClicked = ref<boolean>(false);

    if (
      typeof birthdayFormatted === 'string' &&
      !appStore.getters.appSettings.birthdayUseSystemDatePicker
    ) {
      birthday.value = birthdayFormatted.split('-').reverse().join('.');
    } else if (
      typeof birthdayFormatted !== 'string' &&
      Number.isNaN(birthdayFormatted.getTime())
    ) {
      birthday.value = birthdayFormatted;
    }

    const polls = computed<Poll[]>(() => pollsStore.getters.polls);
    const registrationPoll = computed<Nullable<Poll>>(() =>
      polls.value.find((p) => p.placement === 'registration'),
    );
    const answers = ref<Record<number, { value: Nullable<number> }>>({});

    registrationPoll.value?.fields.forEach((field) => {
      answers.value[field.id] = {
        value: getPollFieldOptionByUserAnswerValue(
          registrationPoll.value,
          field.id,
          getUserAnswer(registrationPoll.value, field.id),
        )?.id,
      };
    });

    const answersInitial: Record<number, { value: Nullable<number> }> = JSON.parse(
      JSON.stringify(answers.value),
    );

    const fieldsComputed = computed<PollField[]>(
      () =>
        registrationPoll.value?.fields?.filter(
          (f) => f.isEditable || (f.isRequired && answersInitial[f.id].value == null),
        ) || [],
    );

    function getUserAnswer(poll: Nullable<Poll>, idField: number): Nullable<string> {
      return appStore.getters.user?.pollAnswers?.find(
        (a) => a.idPoll === poll?.id && a.idPollField === idField,
      )?.value;
    }

    function getPollFieldOptionByUserAnswerValue(
      poll: Nullable<Poll>,
      idField: number,
      value: Nullable<string>,
    ): Nullable<FieldOption> {
      return poll?.fields
        .find((f) => f.id === idField)
        ?.options.find((o) => o.value === value);
    }

    function getPollFieldOptionByUserAnswerId(
      poll: Nullable<Poll>,
      idField: number,
      id: Nullable<number>,
    ): Nullable<FieldOption> {
      return poll?.fields.find((f) => f.id === idField)?.options.find((o) => o.id === id);
    }

    function getFieldOptions(id: number): { name: string; value: number }[] {
      return (
        registrationPoll.value?.fields
          .find((f) => f.id === id)
          ?.options.map((o) => ({ name: o.value, value: o.id })) ?? []
      );
    }

    onMounted(() => {
      android15KeyboardHackFlag.value =
        appStore.getters.isAndroid15 &&
        !!appStore.getters.appSettings.useAndroid15KeyboardHack;

      if (props.callback) {
        const emailInput = refForm.value.querySelector(
          '[data-type=email] input',
        ) as HTMLInputElement;

        if (emailInput) {
          emailInput.focus();
        }
      }
    });

    async function save() {
      buttonClicked.value = true;

      if (validationState.invalid) {
        touchAll();

        const invalidInput = refForm.value.querySelector(
          '.item-input-invalid input',
        ) as HTMLInputElement;

        if (invalidInput) {
          invalidInput.focus();
        }

        return;
      }

      const params: IRegistrationRequest = {
        display_name: (name.value + ' ' + surname.value).trim(),
        bDate: getFormattedDate(birthday.value as any) || '',
        password: password.value,
        email: email.value,
        gender: gender.value,
      };

      f7.preloader.show();

      if (registrationPoll.value) {
        const response: Omit<PollAnswer, 'idPoll'>[] = [];

        Object.keys(answers.value).forEach((key) => {
          const idPollField = parseInt(key);
          const option = getPollFieldOptionByUserAnswerId(
            registrationPoll.value,
            idPollField,
            answers.value[key as any].value,
          );

          if (option) {
            response.push({
              idPollField,
              value: option.value,
            });
          }
        });

        await API.sendResponses({
          idPoll: registrationPoll.value.id,
          response,
        }).catch((e) => {
          // ignore error, do nothing
        });
      }

      await authStore
        .dispatch('updateUser', params)
        .then(() => {
          f7.views.current.router.back();
        })
        .catch((error) => {
          showErrorDialog({ error });
        })
        .finally(() => {
          f7.preloader.hide();

          if (props.callback) {
            props.callback();
          }
        });
    }

    return {
      refForm,
      AuthType,
      validationState,
      onStateChanged,
      isDirty,
      buttonClicked,
      save,
      appSettings: appStore.getters.appSettings,
      settings: authStore.getters.settings,
      name,
      surname,
      gender,
      birthday,
      password,
      email,
      android15KeyboardHackFlag,
      registrationPoll,
      fieldsComputed,
      getFieldOptions,
      answers,
      answersInitial,
    };
  },
});
